import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Music, LogOut, Save, Loader2, ArrowLeft, ListMusic, FileText, ExternalLink, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PUBLIC_URL_FOR = (slug) => (slug === 'artist' ? '/artist' : `/legal/${slug}`);

const AdminPagesPage = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pages`);
      const sorted = data.sort((a, b) => a.slug.localeCompare(b.slug));
      setPages(sorted);
      if (sorted.length && !selected) selectPage(sorted[0]);
    } catch (e) {
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectPage = (p) => {
    setSelected(p);
    setTitle(p.title || '');
    setBody(p.body || '');
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/pages/${selected.slug}`, { title, body });
      setPages(pages.map((p) => (p.slug === data.slug ? data : p)));
      setSelected(data);
      toast.success(`"${data.title}" saved`);
    } catch (e) {
      toast.error('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] py-12 px-4 text-white">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Music className="text-[#00F0FF]" size={28} />
            <div>
              <h1 className="text-3xl font-heading font-medium tracking-tight" data-testid="pages-title">Site Pages</h1>
              <p className="text-[#A1A1AA] text-sm">Edit your Privacy, Terms, Refund, Shipping and Artist Info pages</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {admin?.email && <span className="text-sm text-[#A1A1AA]">{admin.email}</span>}
            <Button
              variant="outline"
              onClick={() => navigate('/admin/releases')}
              className="border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="nav-releases-button"
            >
              <ListMusic size={16} className="mr-2" /> Releases
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/settings')}
              className="border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="nav-settings-button"
            >
              <Settings size={16} className="mr-2" /> Settings
            </Button>
            <Button
              variant="outline"
              onClick={async () => { await logout(); navigate('/'); }}
              className="border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="admin-logout-button"
            >
              <LogOut size={16} className="mr-2" /> Log out
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="text-[#00F0FF] animate-spin" size={40} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6">
            {/* Sidebar */}
            <aside className="bg-[#121214] border border-[#27272A] rounded-lg p-3 h-fit" data-testid="pages-sidebar">
              {pages.map((p) => (
                <button
                  key={p.slug}
                  onClick={() => selectPage(p)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm mb-1 transition-colors flex items-center gap-2 ${selected?.slug === p.slug ? 'bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30' : 'text-[#A1A1AA] hover:text-white hover:bg-[#18181B]'}`}
                  data-testid={`page-tab-${p.slug}`}
                >
                  <FileText size={14} className="flex-shrink-0" />
                  <span className="truncate">{p.title}</span>
                </button>
              ))}
            </aside>

            {/* Editor */}
            {selected && (
              <div className="bg-[#121214] border border-[#27272A] rounded-lg p-6 md:p-8 space-y-5" data-testid="page-editor">
                <div className="flex items-center justify-between gap-4 pb-3 border-b border-[#27272A]">
                  <div>
                    <p className="text-xs text-[#A1A1AA] uppercase tracking-[0.2em]">Editing</p>
                    <p className="font-medium">/{selected.slug === 'artist' ? 'artist' : `legal/${selected.slug}`}</p>
                  </div>
                  <a
                    href={PUBLIC_URL_FOR(selected.slug)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-[#A1A1AA] hover:text-[#00F0FF] inline-flex items-center gap-1"
                    data-testid="preview-page-link"
                  >
                    Preview <ExternalLink size={12} />
                  </a>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Title</label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="bg-[#18181B] border-[#27272A]"
                    data-testid="page-title-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Content</label>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={18}
                    className="w-full px-4 py-3 bg-[#18181B] border border-[#27272A] rounded-md text-sm text-white placeholder:text-[#A1A1AA] focus:outline-none focus:border-[#00F0FF] leading-relaxed"
                    placeholder="Write your content here..."
                    data-testid="page-body-input"
                  />
                  <p className="text-xs text-[#A1A1AA] mt-2">
                    Line breaks are preserved. Full rich formatting can be added later.
                  </p>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-[#00F0FF] hover:bg-[#33F3FF] text-black rounded-full h-11 px-6"
                    data-testid="save-page-button"
                  >
                    {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                    Save changes
                  </Button>
                  <span className="text-xs text-[#A1A1AA]">
                    Last saved: {selected.updated_at ? new Date(selected.updated_at).toLocaleString() : '—'}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPagesPage;
